import { useState } from 'react';
import { useIntl } from 'react-intl';
import ModalDialog from './Modal';
import FileUpload, { FileStatus } from './FileUpload';

interface FileUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (file: File) => Promise<void>;
  title?: string;
  instructions?: string;
  fileType?: string | string[];
  multipleFiles?: boolean;
  submitButtonText?: string;
}

const FileUploadDialog = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  instructions,
  fileType,
  multipleFiles = false,
  submitButtonText,
}: FileUploadDialogProps) => {
  const intl = useIntl();
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileStatuses, setFileStatuses] = useState<FileStatus[]>([]);

  const handleClose = () => {
    setFiles([]);
    setFileStatuses([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (files.length === 0) return;
    setIsSubmitting(true);
    const statuses: FileStatus[] = files.map(() => 'uploading');
    setFileStatuses([...statuses]);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    await Promise.allSettled(
      files.map((file, i) =>
        onSubmit(file).then(
          () => {
            statuses[i] = 'success';
            setFileStatuses([...statuses]);
          },
          () => {
            statuses[i] = 'error';
            setFileStatuses([...statuses]);
          }
        )
      )
    );

    setIsSubmitting(false);
    if (statuses.every((s) => s === 'success')) {
      setTimeout(handleClose, 500);
    }
  };

  return (
    <ModalDialog
      title={title ?? intl.formatMessage({ id: 'uploadFile' })}
      isOpen={isOpen}
      close={handleClose}
      onReady={handleSubmit}
      disabled={files.length === 0 || isSubmitting}
      isSubmitting={isSubmitting}
      submitButtonText={submitButtonText}
      className="enhanced-dialog narrow short padded-minimal"
    >
      <div className="form-group">
        {instructions && <p className="file-upload-instructions">{instructions}</p>}
        <FileUpload
          fileType={fileType}
          multipleFiles={multipleFiles}
          onFiles={(updated) => {
            setFileStatuses((prev) =>
              prev.length === 0
                ? prev
                : updated
                    .map((file) => prev[files.indexOf(file)])
                    .filter((s): s is FileStatus => s !== undefined)
            );
            setFiles(updated);
          }}
          fileStatuses={fileStatuses.length > 0 ? fileStatuses : undefined}
          disabled={isSubmitting}
        />
      </div>
    </ModalDialog>
  );
};

export default FileUploadDialog;
